import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { ProductDao } from '@app/dao/product/product.dao';
import { ProductsTypeEnum } from '@app/forms/subscription/definition/interface';
import { combineLatest, Observable, catchError, distinctUntilChanged, finalize, map, take } from 'rxjs';

import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
import { mapProductType } from '../../services/utile';
import { EaProductInitFormBuilder } from './ea-product-init-form.builder';

import { Validators } from '@angular/forms';
import { AttributionDao } from '@app/dao/attribution/attribution.dao';
import { LoanDao } from '@app/dao/loan/loan.dao';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { ProductGlobalItem } from '@app/forms/subscription/definition/interface/product';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { API_GRIDKY_SEARCH } from '../../api/api-router';
import { productsType } from '../../services/ref';

interface ProductOptions {
  type: string[];
  onlineSubscription?: boolean;
  mappedSubscriptionForm?: boolean | null;
  sorts: SortOption[];
  organizationIds: number[];
  isCrowdfundingAvailableInSubscription?: boolean;
  size?: number;
  isGirardinAvailableInSubscription?: boolean;
  isPrivateEquityAvailableInSubscription?: boolean;
  disponibleInOrganization?: boolean;
}

interface SortOption {
  property: string;
  direction: 'ASC' | 'DESC';
}
@UntilDestroy()
@Component({
    selector: 'product-init',
    templateUrl: './product-init.component.html',
    styleUrls: ['./product-init.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ProductInitComponent
  extends EaProductInitFormBuilder
  implements OnInit
{
  products$: Observable<ProductGlobalItem[]>;
  gridky$: Observable<any[]>;
  loans$: Observable<any[]>;
  products: ProductGlobalItem[] = [];
  availableOperations: any[] = [];
  chooseOperation: string = 'SUBSCRIPTION';
  clientAssets: any[] = [];
  loansCategoriesRefs: any[] = [];
  owners: any[] = [];
  objectMapper = {
    ...productsType,
    HOUSE: 'maison',
    APPARTMENT: 'appartement',
  };
  productType;
  loadingData: boolean = true;
  isConsultant: boolean = false;
  organizationId: number = 1;
  accountId: number;
  additionalOptionLabel: {
    name: string;
    useParentheses: boolean;
    useDash: boolean;
  }[] = [
    { name: 'realestateType', useParentheses: false, useDash: true },
    { name: 'type', useParentheses: true, useDash: false },
  ];
  @Output() productEvent = new EventEmitter<{
    product: ProductGlobalItem;
    gridkyPropertyId: number;
    loanId: number;
    borrowerInsuranceContext: string;
    operation: string;
    operationAssetContractId: number;
    borrowerInsuranceMode: string;
    allowedApiFlux: boolean;
  }>();
  typeMapper = {
    apartment: 'appartement',
    house: 'maison',
  };

  constructor(
    protected formConstructor: PrFormConstructor,
    private productsDao: ProductDao,
    public route: ActivatedRoute,
    public loanDao: LoanDao,
    private subscriptionFacade: SubscriptionFacadeService,
    private httpApi: HttpApiService,
    private attributionDao: AttributionDao,
    private referenceDao: ReferenceDao,
    private cd: ChangeDetectorRef,
  ) {
    super(formConstructor);
  }

  ngOnInit(): void {
    const {
      productType,
      operationAssetContractId,
      operation,
      operationProductId,
      submitFromOperations,
      submitFromOptimization,
      loanId,
    } = this.route.snapshot.queryParams;

    this.chooseOperation = operation || 'SUBSCRIPTION';

    if (submitFromOptimization) {
      this.onSubmitFromOptimization(loanId, operationProductId);
    }

    if (submitFromOperations) {
      this.onSubmitFromOperation(
        productType,
        operationAssetContractId,
        this.chooseOperation,
        operationProductId,
      );
    }

    this.productType = mapProductType(productType);

    this.isConsultant = !!this.subscriptionFacade?.getUser()?.sessionConsultant;
    this.accountId = this.subscriptionFacade?.getUser()?.accountId;
    const { type } = this.getProductCategoriesAndType();

    combineLatest([
      this.attributionDao.getOrganization().pipe(take(1)),
      this.referenceDao.getAvailableOperations().pipe(take(1)),
      this.productsDao
        .getProductsGeneral(
          this.getProductOptions(type, this.chooseOperation !== 'SUBSCRIPTION'),
        )
        .pipe(take(1)),
    ]).subscribe({
      next: ([attribution, availableOperations, products]) => {
        this.organizationId = attribution?.id;
        this.availableOperations = availableOperations || [];
        this.products = products || [];
      },
      error: err => {
        console.error('Erreur lors du chargement des données', err);
      },
      complete: () => {
        this.loadingData = false;
        this.cd.markForCheck();
      },
    });

    this.initForm({
      operation: this.chooseOperation,
    });
    this.formProductValueChanges();
  }

  onSubmitFromOperation(
    productType,
    operationAssetContractId,
    operation,
    operationProductId,
  ) {
    if (
      productType &&
      operationAssetContractId &&
      operation &&
      operationProductId
    ) {
      this.productEvent.emit({
        operation,
        product: {
          id: operationProductId,
          type: productType,
        } as ProductGlobalItem,
        operationAssetContractId,
        gridkyPropertyId: null,
        allowedApiFlux: false,
        loanId: null,
        borrowerInsuranceContext: null,
        borrowerInsuranceMode: null,
      });
    }
  }

  onSubmitFromOptimization(loanId, operationProductId) {
    if (loanId) {
      this.productEvent.emit({
        operation: 'SUBSCRIPTION',
        product: {
          id: operationProductId,
          type: 'BORROWER_INSURANCE',
        } as ProductGlobalItem,
        operationAssetContractId: null,
        gridkyPropertyId: null,
        allowedApiFlux: false,
        loanId,
        borrowerInsuranceContext: 'CHANGE',
        borrowerInsuranceMode: 'API',
      });
    }
  }

  private formProductValueChanges() {
    this.form
      ?.get('product')
      .valueChanges.pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(product => {
        if (
          product?.id &&
          this.isUnfurnishedOrLMP_LMNP() &&
          this.isGridky(product)
        ) {
          this.gridky$ = this.getGridKy(product);
        }
        if (product?.id && this.isBorrowerInsurance()) {
          if (!!product?.subscriptionFluxAllowed) {
            this.form?.get('allowedApiFlux').setValue(true);
            this.form?.get('borrowerInsuranceMode').setValue('API');
          } else {
            this.form?.get('allowedApiFlux').setValue(false);
            this.form?.get('borrowerInsuranceMode').setValue('MANUAL');
          }

          this.form?.get('loanId').setValidators([Validators.required]);
          this.form?.get('loanId').updateValueAndValidity();
          this.form
            ?.get('borrowerInsuranceContext')
            .setValidators([Validators.required]);
          this.form?.get('borrowerInsuranceContext').updateValueAndValidity();
          this.form
            ?.get('borrowerInsuranceMode')
            .setValidators([Validators.required]);
          this.form?.get('borrowerInsuranceMode').updateValueAndValidity();
          this.loans$ = this.loanDao.getLoans();
          this.loanDao.getReferences().subscribe(data => {
            this.loansCategoriesRefs = data?.categories;
          });
        }
      });
  }

  private getGridKy(product: ProductGlobalItem) {
    this.loading = true;
    const gridkyProgramId = product?.gridkyProgramId;

    if (gridkyProgramId)
      return this.httpApi
        .post(API_GRIDKY_SEARCH, {
          programId: product?.gridkyProgramId,
        })
        .pipe(
          map(res => {
            return res?.content;
          }),
          map(content => content.filter(el => !!el?.surface)),
          catchError(err => {
            console.error(err);
            this.loading = false;
            throw err;
          }),
          finalize(() => {
            this.loading = false;
          }),
        );
  }
  private isUnfurnishedOrLMP_LMNP(): boolean {
    return [ProductsTypeEnum.UNFURNISHED, ProductsTypeEnum.LMNP].includes(
      this.productType,
    );
  }
  private isGridky(product: ProductGlobalItem): boolean {
    return !!product?.gridkyProgramId;
  }
  private isBorrowerInsurance(): boolean {
    return [ProductsTypeEnum.BORROWER_INSURANCE].includes(this.productType);
  }
  private getProductCategoriesAndType(): {
    type: ProductsTypeEnum[];
  } {
    let type: ProductsTypeEnum[] = [this.productType];
    return { type };
  }
  private getProductOptions(
    type: ProductsTypeEnum[],
    fromOperations: boolean,
  ): ProductOptions {
    const isCrowfundingSubscription =
      type.length === 1 && type[0] === ProductsTypeEnum.CROWDFUNDING;
    const isGirardinSubscription =
      type.length === 1 && type[0] === ProductsTypeEnum.GIRARDIN;
    const isPrivateEquitySubscription =
      type.length === 1 &&
      [
        ProductsTypeEnum.FPCR,
        ProductsTypeEnum.FPCI,
        ProductsTypeEnum.FCPI,
        ProductsTypeEnum.FCPR,
        ProductsTypeEnum.FPS,
        ProductsTypeEnum.PME,
        ProductsTypeEnum.FIP,
        ProductsTypeEnum.FIP_CORSE,
        ProductsTypeEnum.FIP_DOMTOM,
      ].includes(type[0]);

    let sorts: SortOption[] = [{ property: 'NAME', direction: 'ASC' }];

    if (isCrowfundingSubscription || isGirardinSubscription) {
      sorts.unshift({ property: 'ALLOWED_FLUX', direction: 'DESC' });
    }

    if (fromOperations) {
      return {
        type,
        size: -1,
        organizationIds: [this.organizationId],
        sorts,
      };
    }

    return {
      type,
      onlineSubscription: true,
      mappedSubscriptionForm: this.isConsultant ? null : true,
      organizationIds: [this.organizationId],
      sorts,
      isCrowdfundingAvailableInSubscription: isCrowfundingSubscription
        ? true
        : undefined,
      size: -1, //no limit
      isGirardinAvailableInSubscription: isGirardinSubscription
        ? true
        : undefined,
      isPrivateEquityAvailableInSubscription: isPrivateEquitySubscription
        ? true
        : undefined,
      disponibleInOrganization: true,
    };
  }
  onSubmit() {
    this.displayErrors();

    if (this.form.valid) {
      this.productEvent.emit({
        operation: this.form.value?.operation,
        product: this.form.value?.product,
        gridkyPropertyId: this.form.value?.gridkyPropertyId,
        allowedApiFlux: this.form.value?.allowedApiFlux,
        loanId:
          this.form.value.loanId && this.form.value.loanId === 'AUTRE'
            ? null
            : this.form.value.loanId,
        borrowerInsuranceContext: this.form.value?.borrowerInsuranceContext,
        borrowerInsuranceMode: this.form.value?.borrowerInsuranceMode,
        operationAssetContractId: this.form.value?.operationAssetContractId,
      });
    }
  }
  prev() {}

  getIcon(option: ProductGlobalItem) {
    const icons = [];
    if (option && option.electronicSignature) icons.push('fingerprint');

    if (option && option.mappedSubscriptionForm) icons.push('task');

    return icons;
  }
  onSelect(gridky) {}

  getLoanCategoryName(id) {
    return this.loansCategoriesRefs.find(el => el.id === id)?.value;
  }
}
